"use client";

import {
  type HTMLAttributes,
  type InputEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  type KeyboardEvent,
  type ChangeEventHandler,
  type FC,
  type Ref,
} from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { debounce, mergeClasses } from "@huub-web/utils";

import type { ClassNameProp } from "../../props";
import { textAreaStyle } from "./PlainTextArea.css";
import { typography } from "../../theme/css/typography.css";

export type HTMLTextAreaElementWithReset = HTMLTextAreaElement & {
  reset: () => void;
};

export type TextAreaProps = HTMLAttributes<HTMLTextAreaElement> &
  ClassNameProp &
  RecipeVariants<typeof textAreaStyle> & {
    disabled?: boolean;
    placeholder?: string;
    value?: string;
    onEnterKey?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLTextAreaElement>;
  };

/**
 * A textarea component.
 */
export const PlainTextArea: FC<TextAreaProps> = ({
  value,
  onInput,
  onEnterKey,
  onChange,
  className,
  ref,
  ...props
}) => {
  const textareaRef = useRef<HTMLTextAreaElementWithReset>(null);

  useImperativeHandle(ref, () => {
    const textarea = textareaRef.current;
    if (!textarea) {
      throw new Error("TextArea ref is not available");
    }
    return {
      ...textarea,
      reset: () => {
        textarea.value = "";
        handleResize();
      },
      focus: () => {
        textarea.focus();
      },
    };
  });

  const handleResize = () => {
    if (!textareaRef.current) {
      return;
    }

    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  };

  const debouncedResize = debounce(handleResize, 200);

  useEffect(() => {
    if (!textareaRef.current) {
      return;
    }
    handleResize();

    const observer = new ResizeObserver(() => debouncedResize());
    observer.observe(textareaRef.current);

    return () => {
      if (!textareaRef.current) {
        return;
      }
      observer.unobserve(textareaRef.current);
    };
  }, []);

  const handleInput = (e: InputEvent<HTMLTextAreaElement>) => {
    onInput?.(e);
    handleResize();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onEnterKey?.(e);
    }
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    onChange?.(e);
    handleResize();
  };

  return (
    <textarea
      ref={textareaRef}
      rows={1}
      className={mergeClasses(
        textAreaStyle({ disabled: props.disabled }),
        className,
        typography.bodyM,
      )}
      onChange={handleChange}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      value={value ?? ""}
      autoComplete="off"
      data-1p-ignore="true"
      {...props}
    />
  );
};
